import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";

import {
  LeadValidationError,
  queuedLeadCount,
  startLeadSync,
  submitLead,
} from "@/features/leadCapture";

/**
 * TCGMA-175 — public signup / prize-draw entry, built for a trade-show table.
 *
 * Reached two ways, both of which set the search params:
 *   - QR code on the table  → /shop/join?event=Seattle%20Card%20Show&source=qr
 *   - the booth tablet      → /shop/join?event=…&source=kiosk
 *
 * `event` is what makes the leads worth anything — it tells the operator which
 * show produced which signups.
 *
 * Booth-specific design decisions (each of these is a real failure otherwise):
 *   - autoComplete OFF everywhere. On a shared tablet the browser would happily
 *     offer visitor #1's email to visitor #2. That's a privacy incident.
 *   - The form fully resets after each submit, and on a kiosk it auto-returns to
 *     a blank form so the next person walks up to a clean slate.
 *   - A submit that can't reach the server still reads as success — it's queued
 *     and synced later (see features/leadCapture). The visitor is standing right
 *     there; "try again" is not an option.
 *   - No email round-trip. Nobody digs a magic link out of their inbox at your
 *     table — we capture the address and mail them afterwards.
 */

const SearchParams = (
  search: Record<string, unknown>,
): { event?: string; source?: "kiosk" | "qr" } => ({
  ...(typeof search.event === "string" ? { event: search.event } : {}),
  ...(search.source === "kiosk" || search.source === "qr"
    ? { source: search.source }
    : {}),
});

export const Route = createFileRoute("/shop/join")({
  component: ShopJoin,
  validateSearch: SearchParams,
});

const GAMES = ["Pokémon", "Magic", "Other"];

// How long the "you're in" screen stays up on a kiosk before resetting for the
// next person in line.
const KIOSK_RESET_MS = 8000;

function ShopJoin() {
  const { event, source } = Route.useSearch();
  const isKiosk = source === "kiosk";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [interests, setInterests] = useState<string[]>([]);
  const [consent, setConsent] = useState(false);
  const [company, setCompany] = useState(""); // honeypot — humans never see this
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [queued, setQueued] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(0);
  const nameRef = useRef<HTMLInputElement>(null);

  // Replay anything the outbox is holding, now and whenever we come back online.
  useEffect(() => {
    const stop = startLeadSync(() => setPending(queuedLeadCount()));
    setPending(queuedLeadCount());
    return stop;
  }, []);

  const reset = () => {
    setName("");
    setEmail("");
    setInterests([]);
    setConsent(false);
    setCompany("");
    setError(null);
    setQueued(false);
    setDone(false);
    nameRef.current?.focus();
  };

  // On a kiosk, clear the confirmation automatically so the next person doesn't
  // walk up to the previous person's "thanks, Dave!" screen.
  useEffect(() => {
    if (!done || !isKiosk) return;
    const t = setTimeout(reset, KIOSK_RESET_MS);
    return () => clearTimeout(t);
  }, [done, isKiosk]);

  const toggleInterest = (game: string) =>
    setInterests((cur) =>
      cur.includes(game) ? cur.filter((g) => g !== game) : [...cur, game],
    );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("Please enter a valid email.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitLead({
        email: trimmed,
        name: name.trim() || null,
        interests,
        marketingConsent: consent,
        raffleEntry: true, // this page IS the draw entry
        eventName: event ?? null,
        source: source ?? "web",
        // Honeypot travels as `company` — the server discards any lead with it set.
        ...(company ? { company } : {}),
      });
      setQueued(result.status === "queued");
      setPending(queuedLeadCount());
      setDone(true);
    } catch (err) {
      setError(
        err instanceof LeadValidationError
          ? err.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-md space-y-5 py-16 text-center">
        <h1 className="shop-display text-3xl font-bold text-foreground">
          You're in! 🎉
        </h1>
        <p className="text-base text-muted-foreground">
          You're entered in the draw{event ? ` at ${event}` : ""}. We'll be in
          touch — good luck!
        </p>
        {queued && (
          <p className="text-xs text-muted-foreground">
            (Saved on this device — it'll sync as soon as we're back online.)
          </p>
        )}
        <button
          type="button"
          onClick={reset}
          className="w-full rounded-md bg-primary px-4 py-4 text-base font-medium text-primary-foreground hover:bg-primary/90"
        >
          {isKiosk ? "Next person" : "Sign up someone else"}
        </button>
        {!isKiosk && (
          <p className="text-center text-xs text-muted-foreground">
            <Link to="/shop/inventory" className="hover:text-foreground">
              ← Browse the store
            </Link>
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md space-y-6 py-10">
      <div className="space-y-1 text-center">
        <h1 className="shop-display text-3xl font-bold text-foreground">
          Enter the draw
        </h1>
        <p className="text-sm text-muted-foreground">
          {event ? `${event} — f` : "F"}ree to enter, no purchase necessary.
          Drop your email and you're in.
        </p>
      </div>

      <form
        onSubmit={submit}
        // Shared booth device: never let the browser offer the last person's
        // details to the next one.
        autoComplete="off"
        className="space-y-5 rounded-lg border border-border bg-card p-5"
      >
        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-foreground">Name</span>
          <input
            ref={nameRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="off"
            // 16px+ so iOS doesn't zoom the viewport on focus.
            className="w-full rounded-md border border-border bg-background px-3 py-3 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Your name"
          />
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-foreground">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="off"
            inputMode="email"
            required
            className="w-full rounded-md border border-border bg-background px-3 py-3 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="you@example.com"
          />
        </label>

        <div className="space-y-1.5">
          <span className="text-sm font-medium text-foreground">
            What do you collect?
          </span>
          <div className="flex gap-2">
            {GAMES.map((game) => {
              const on = interests.includes(game);
              return (
                <button
                  key={game}
                  type="button"
                  onClick={() => toggleInterest(game)}
                  aria-pressed={on}
                  className={`flex-1 rounded-md border px-3 py-3 text-sm font-medium transition ${
                    on
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-background text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {game}
                </button>
              );
            })}
          </div>
        </div>

        {/* Honeypot: hidden from humans, catnip for bots. There's no email
            round-trip on this form, so this + the rate limit are the spam gate. */}
        <div aria-hidden="true" className="hidden">
          <label>
            Company
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </label>
        </div>

        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-1 h-5 w-5 rounded border-border text-primary focus:ring-primary"
          />
          <span className="text-sm text-muted-foreground">
            Email me about new cards and deals. (Optional — you're entered in
            the draw either way.)
          </span>
        </label>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-md bg-primary px-4 py-4 text-base font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          {submitting ? "Entering…" : "Enter the draw"}
        </button>

        <p className="text-center text-xs text-muted-foreground">
          We'll never sell your details. Unsubscribe any time.
        </p>
      </form>

      {pending > 0 && (
        <p className="text-center text-xs text-muted-foreground">
          {pending} signup{pending === 1 ? "" : "s"} saved offline — syncing
          when the connection returns.
        </p>
      )}
    </div>
  );
}
